import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { X, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMOJI_AVATARS = ['👤', '👨', '👩', '👦', '👧', '👶', '🧒', '👴', '👵', '💼', '👨‍⚕️', '👩‍⚕️'];
const HEALTH_GOALS = [
  'Weight Management',
  'Better Sleep',
  'More Energy',
  'Reduce Stress',
  'Healthy Eating',
  'Regular Exercise',
  'Preventive Health',
  'Mental Wellness',
  'Chronic Disease Management',
  'Immunization Tracking'
];

const AddFamilyMemberModal = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    relationship: '',
    age: '',
    gender: '',
    health_goals: [],
    avatar_emoji: '👤',
    notes: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleHealthGoal = (goal) => {
    setFormData(prev => ({
      ...prev,
      health_goals: prev.health_goals.includes(goal)
        ? prev.health_goals.filter(g => g !== goal)
        : [...prev.health_goals, goal]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.relationship) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      // Auth via httpOnly cookie — no localStorage token needed.
      const payload = {
        ...formData,
        age: formData.age ? parseInt(formData.age) : null,
        is_primary: formData.relationship === 'self'
      };

      await axios.post(`${API}/family-health/members`, payload, {
        withCredentials: true,
      });

      toast.success(`${formData.name} added to family!`);
      onSuccess();
      onClose();
      
      // Reset form
      setFormData({
        name: '',
        relationship: '',
        age: '',
        gender: '',
        health_goals: [],
        avatar_emoji: '👤',
        notes: ''
      });
    } catch (error) {
      console.error('Error adding family member:', error);
      toast.error('Failed to add family member');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Add Family Member
          </DialogTitle>
          <DialogDescription>
            Add a family member to track their preventive health journey
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Avatar Selection */}
          <div>
            <Label>Choose Avatar</Label>
            <div className="flex gap-2 flex-wrap mt-2">
              {EMOJI_AVATARS.map(emoji => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => handleChange('avatar_emoji', emoji)}
                  className={`text-2xl p-2 rounded-lg border-2 transition-all ${
                    formData.avatar_emoji === emoji
                      ? 'border-blue-500 bg-blue-50 scale-110'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., Sarah"
                required
              />
            </div>

            <div>
              <Label htmlFor="relationship">Relationship *</Label>
              <Select value={formData.relationship} onValueChange={(val) => handleChange('relationship', val)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select relationship" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">Self (Me)</SelectItem>
                  <SelectItem value="partner">Partner/Spouse</SelectItem>
                  <SelectItem value="child">Child</SelectItem>
                  <SelectItem value="parent">Parent</SelectItem>
                  <SelectItem value="sibling">Sibling</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="age">Age</Label>
              <Input
                id="age"
                type="number"
                value={formData.age}
                onChange={(e) => handleChange('age', e.target.value)}
                placeholder="e.g., 28"
                min="0"
                max="120"
              />
            </div>

            <div>
              <Label htmlFor="gender">Gender (optional)</Label>
              <Select value={formData.gender} onValueChange={(val) => handleChange('gender', val)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                  <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Health Goals */}
          <div>
            <Label>Health Goals (select all that apply)</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {HEALTH_GOALS.map(goal => (
                <Badge
                  key={goal}
                  variant={formData.health_goals.includes(goal) ? 'default' : 'outline'}
                  className="cursor-pointer hover:scale-105 transition-transform"
                  onClick={() => toggleHealthGoal(goal)}
                >
                  {formData.health_goals.includes(goal) && <span className="mr-1">✓</span>}
                  {goal}
                </Badge>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notes (optional)</Label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Any additional health information, allergies, or notes..."
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Adding...' : 'Add Member'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddFamilyMemberModal;
